import { useEffect, useRef } from 'react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { SafeNavigate } from '@/services/router/safe-navigate';
import type { Permission } from '@customerio/hydra-permissions';
import { useSnackbars } from '@customerio/pluma-components/react';
import { useAuth } from '@/services/auth';
import { useParams } from '@/services/router/compat';

type RouteParams = Record<string, string | undefined>;

interface ProtectedRouteProps {
  permission: Permission | Permission[];
  redirectTo: string | ((params: RouteParams) => string);
  children: React.ReactNode;
  errorMessage?: string;
  allowReadOnlyMasqAccess?: boolean;
}

export function ProtectedRoute({
  permission,
  redirectTo,
  children,
  errorMessage = "You're not allowed to view this page. Contact an Admin to update your role.",
  allowReadOnlyMasqAccess = false,
}: ProtectedRouteProps) {
  const { hasPermission, hasAllPermissions } = usePermissions();
  const auth = useAuth();
  const snackbars = useSnackbars();
  const params = useParams({ strict: false });
  const hasShownSnackbarRef = useRef(false);

  const permissionResult = Array.isArray(permission) ? hasAllPermissions(...permission) : hasPermission(permission);

  const isMasqWithReadAccess =
    auth.account?.is_masq === true &&
    (auth.user?.canReadOnlyMasq === true || auth.user?.canReadWriteMasq === true || auth.user?.isSuperUser === true);

  const isAllowed = permissionResult === true || (allowReadOnlyMasqAccess && isMasqWithReadAccess);
  const isDenied = !isAllowed;

  useEffect(() => {
    if (isDenied) {
      if (!hasShownSnackbarRef.current) {
        snackbars.create({
          message: errorMessage,
          variant: 'error',
        });
        hasShownSnackbarRef.current = true;
      }
    } else {
      hasShownSnackbarRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDenied]);

  if (isDenied) {
    const resolvedRedirectTo = typeof redirectTo === 'function' ? redirectTo(params) : redirectTo;
    return <SafeNavigate to={resolvedRedirectTo} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return <>{children}</>;
}
